/**
 * Growth KPIs: sign-ups over time, file/download activity, onboarding segments.
 */

import { contactCreatedAtMs } from "./db";
import {
  monthToDateEquivalentRanges,
  compareEquivalentPeriods,
  countInRange,
} from "./periodCompare";
import { orderStatusLabel } from "./orderStatus";

/** Sign-up time for analytics — matches Contacts `fmtDate(createdAt)` (no updatedAt fallback). */
function signupTimestampMs(contact) {
  return contactCreatedAtMs(contact);
}
import {
  formatWorkRole,
  formatProjectTypes,
  formatPreferredFormat,
  WORK_ROLE_LABELS,
  PROJECT_TYPE_LABELS,
  PREFERRED_FORMAT_LABELS,
} from "./contactProfileFields";

const MS_DAY = 86400000;

function tsMs(v) {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Local calendar day — matches Contacts `fmtDate(createdAt)` (browser timezone). */
export function localDayKey(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfLocalDayMs(ms) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function monthKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function quarterKey(ms) {
  const d = new Date(ms);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

function quarterLabel(key) {
  return key.replace("-", " ");
}

/** True when the doc looks like an explicit download event (not a generic upload). */
export function isDownloadEventDoc(doc) {
  if (!doc || typeof doc !== "object") return false;
  const type = String(
    doc.type ?? doc.event_type ?? doc.eventType ?? doc.action ?? doc.kind ?? ""
  ).toLowerCase();
  if (type.includes("download")) return true;
  if (doc.downloaded_at != null || doc.downloadedAt != null) return true;
  const n = Number(doc.download_count ?? doc.downloadCount ?? 0);
  if (Number.isFinite(n) && n > 0) return true;
  return false;
}

function fileActivityMs(doc) {
  return tsMs(
    doc?.downloaded_at ??
      doc?.downloadedAt ??
      doc?.createdAt ??
      doc?.created_at ??
      doc?.uploadedAt ??
      doc?.uploaded_at ??
      doc?.timestamp
  );
}

function incrementMap(map, key, delta = 1) {
  if (!key) return;
  map.set(key, (map.get(key) || 0) + delta);
}

function normalizeListValue(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean);
  const s = String(raw).trim();
  if (!s) return [];
  if (s.includes(",")) return s.split(",").map((v) => v.trim()).filter(Boolean);
  return [s];
}

function slugLabel(value, labelMap) {
  if (!value) return "";
  const v = String(value).trim();
  return labelMap[v] || labelMap[v.toLowerCase()] || v.replace(/_/g, " ");
}

export function segmentProfession(contact) {
  const formatted = formatWorkRole(contact.workRole, contact.workRoleOther);
  return formatted || "Not set";
}

function segmentPreferredFormat(contact) {
  const formatted = formatPreferredFormat(contact.preferredFormat, contact.preferredFormatOther);
  return formatted || "Not set";
}

function segmentProjectTypes(contact) {
  const formatted = formatProjectTypes(contact.projectTypes, contact.projectTypeOther);
  if (!formatted) return ["Not set"];
  return formatted.split(",").map((s) => s.trim()).filter(Boolean);
}

function segmentRole(contact) {
  const job = contact.role != null ? String(contact.role).trim() : "";
  if (job) return job;
  const profession = segmentProfession(contact);
  return profession !== "Not set" ? profession : "Not set";
}

/** @returns {{ label: string, count: number, pct: number }[]} sorted desc */
export function buildSegmentBreakdown(signups, labelFn, { maxRows = 12 } = {}) {
  const map = new Map();
  for (const c of signups) {
    const labels = labelFn(c);
    const list = Array.isArray(labels) ? labels : [labels];
    for (const label of list) {
      const key = label || "Not set";
      incrementMap(map, key);
    }
  }
  const total = signups.length || 1;
  const rows = [...map.entries()]
    .map(([label, count]) => ({
      label,
      count,
      pct: (count / total) * 100,
    }))
    .sort((a, b) => b.count - a.count);
  if (rows.length <= maxRows) return rows;
  const head = rows.slice(0, maxRows - 1);
  const rest = rows.slice(maxRows - 1);
  const otherCount = rest.reduce((s, r) => s + r.count, 0);
  head.push({ label: "Other", count: otherCount, pct: (otherCount / total) * 100 });
  return head;
}

function avgPerDayInRange(countByDay, fromMs, toMs) {
  if (!countByDay.size || toMs < fromMs) return null;
  let sum = 0;
  let days = 0;
  for (let t = startOfLocalDayMs(fromMs); t <= startOfLocalDayMs(toMs); t += MS_DAY) {
    sum += countByDay.get(localDayKey(t)) || 0;
    days += 1;
  }
  return days > 0 ? sum / days : null;
}

function sumInDayRange(countByDay, fromMs, toMs) {
  let sum = 0;
  for (let t = startOfLocalDayMs(fromMs); t <= startOfLocalDayMs(toMs); t += MS_DAY) {
    sum += countByDay.get(localDayKey(t)) || 0;
  }
  return sum;
}

function pctChange(current, previous) {
  if (previous == null || previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

/**
 * @param {object[]} contacts CRM contacts (users)
 * @param {object[]} [files] Firestore `files` collection
 */
/** Daily cumulative sign-ups: this month vs same calendar days last month. */
export function buildMonthSignupComparisonSeries(dailySignups, now = Date.now()) {
  const d = new Date(now);
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const prevM = m === 0 ? 11 : m - 1;
  const prevY = m === 0 ? y - 1 : y;
  const labels = [];
  const current = [];
  const previous = [];
  for (let i = 1; i <= day; i++) {
    labels.push(String(i));
    const curKey = localDayKey(new Date(y, m, i).getTime());
    const prevKey = localDayKey(new Date(prevY, prevM, i).getTime());
    current.push(dailySignups.get(curKey) || 0);
    previous.push(dailySignups.get(prevKey) || 0);
  }
  return { labels, current, previous };
}

export function computeGrowthAnalytics(contacts = [], files = [], opts = {}) {
  const { payments = [], orders = [] } = opts;
  const signups = contacts.filter((c) => !c.isInternal);
  const now = Date.now();
  const todayStart = startOfLocalDayMs(now);
  const todayKey = localDayKey(now);
  const timezoneLabel =
    typeof Intl !== "undefined" && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "local";

  const dailySignups = new Map();
  const monthlySignups = new Map();
  const quarterlySignups = new Map();
  let earliestSignupMs = null;

  for (const c of signups) {
    const ms = signupTimestampMs(c);
    if (!ms || ms <= 0) continue;
    if (earliestSignupMs == null || ms < earliestSignupMs) earliestSignupMs = ms;
    incrementMap(dailySignups, localDayKey(ms));
    incrementMap(monthlySignups, monthKey(ms));
    incrementMap(quarterlySignups, quarterKey(ms));
  }

  const dailySignupsHistory = [...dailySignups.entries()]
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const peakDay =
    dailySignupsHistory.length === 0
      ? null
      : dailySignupsHistory.reduce((best, row) => (row.count > best.count ? row : best), dailySignupsHistory[0]);

  const last30Start = todayStart - 29 * MS_DAY;
  const last7Start = todayStart - 6 * MS_DAY;
  const prior7Start = todayStart - 13 * MS_DAY;
  const prior7End = todayStart - 7 * MS_DAY;

  const signupsToday = dailySignups.get(todayKey) || 0;
  const signupsLast7 = sumInDayRange(dailySignups, last7Start, todayStart);
  const signupsPrior7 = sumInDayRange(dailySignups, prior7Start, prior7End);
  const signupsLast30 = sumInDayRange(dailySignups, last30Start, todayStart);
  const avgDailySignups30d = avgPerDayInRange(dailySignups, last30Start, todayStart);

  const thisMonthKey = monthKey(now);
  const lastMonthDate = new Date(now);
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonthKey = monthKey(lastMonthDate.getTime());

  const signupsThisMonth = monthlySignups.get(thisMonthKey) || 0;
  const signupsLastMonth = monthlySignups.get(lastMonthKey) || 0;

  const mtdRanges = monthToDateEquivalentRanges(now);
  const signupMtdEquivalent = compareEquivalentPeriods(
    countInRange(signups, signupTimestampMs, mtdRanges.current.start, mtdRanges.current.end),
    countInRange(signups, signupTimestampMs, mtdRanges.previous.start, mtdRanges.previous.end),
    { current: mtdRanges.labelCurrent, previous: mtdRanges.labelPrevious }
  );
  const momSignupGrowthPct = signupMtdEquivalent.growthPct;
  const monthSignupComparison = buildMonthSignupComparisonSeries(dailySignups, now);

  const thisQuarterKey = quarterKey(now);
  const signupsThisQuarter = quarterlySignups.get(thisQuarterKey) || 0;

  const monthlySignupsSeries = [...monthlySignups.entries()]
    .map(([key, count]) => ({ key, label: monthLabel(key), count }))
    .sort((a, b) => a.key.localeCompare(b.key))
    .slice(-14);

  const quarterlySignupsSeries = [...quarterlySignups.entries()]
    .map(([key, count]) => ({ key, label: quarterLabel(key), count }))
    .sort((a, b) => a.key.localeCompare(b.key))
    .slice(-8);

  const explicitDownloads = (files || []).filter(isDownloadEventDoc);
  const downloadDocs = explicitDownloads.length > 0 ? explicitDownloads : files || [];
  const downloadsAreExplicit = explicitDownloads.length > 0;

  const dailyDownloads = new Map();
  let earliestDownloadMs = null;
  for (const doc of downloadDocs) {
    const ms = fileActivityMs(doc);
    if (!ms || ms <= 0) continue;
    if (earliestDownloadMs == null || ms < earliestDownloadMs) earliestDownloadMs = ms;
    const weight = isDownloadEventDoc(doc)
      ? Math.max(1, Number(doc.download_count ?? doc.downloadCount ?? 1) || 1)
      : 1;
    incrementMap(dailyDownloads, localDayKey(ms), weight);
  }

  const spanStart =
    earliestDownloadMs != null && earliestSignupMs != null
      ? Math.min(earliestDownloadMs, earliestSignupMs)
      : earliestDownloadMs ?? earliestSignupMs ?? last30Start;
  const overallFrom = startOfLocalDayMs(spanStart);
  const totalDownloads = [...dailyDownloads.values()].reduce((s, n) => s + n, 0);
  const overallDays = Math.max(1, Math.floor((todayStart - overallFrom) / MS_DAY) + 1);
  const avgDailyDownloadsOverall = totalDownloads / overallDays;

  const nowLocal = new Date(now);
  const monthStart = new Date(nowLocal.getFullYear(), nowLocal.getMonth(), 1).getTime();
  const downloadsThisMonth = sumInDayRange(dailyDownloads, monthStart, todayStart);
  const daysThisMonth = Math.max(1, Math.floor((todayStart - monthStart) / MS_DAY) + 1);
  const avgDailyDownloadsThisMonth = downloadsThisMonth / daysThisMonth;

  const withProfession = signups.filter((c) => c.workRole && String(c.workRole).trim()).length;
  const withFormat = signups.filter((c) => c.preferredFormat && String(c.preferredFormat).trim()).length;
  const withProjectTypes = signups.filter((c) => normalizeListValue(c.projectTypes).length > 0).length;
  const withEmail = signups.filter((c) => c.email && String(c.email).includes("@")).length;

  const orderStatusSegments = buildSegmentBreakdown(signups, (c) => orderStatusLabel(c, orders, payments));
  const planSegments = orderStatusSegments;
  const industrySegments = buildSegmentBreakdown(signups, (c) => c.industry?.trim() || "Not set");

  return {
    signupsTotal: signups.length,
    signupsToday,
    signupsTodayKey: todayKey,
    timezoneLabel,
    signupsLast7,
    signupsPrior7,
    wowSignupGrowthPct: pctChange(signupsLast7, signupsPrior7),
    signupsLast30,
    avgDailySignups30d,
    signupsThisMonth,
    signupsLastMonth,
    signupMtdEquivalent,
    monthSignupComparison,
    momSignupGrowthPct,
    signupsThisQuarter,
    dailySignupsHistory,
    dailySignupsLast90: dailySignupsHistory.filter((r) => {
      // Parse as local calendar day (keys are from localDayKey), not UTC midnight.
      const [y, m, d] = String(r.date || "").split("-").map(Number);
      if (!y || !m || !d) return false;
      const ms = new Date(y, m - 1, d).getTime();
      return ms >= todayStart - 89 * MS_DAY;
    }),
    peakDay,
    monthlySignupsSeries,
    quarterlySignupsSeries,
    monthlySignupsMap: monthlySignups,
    quarterlySignupsMap: quarterlySignups,
    totalDownloads,
    avgDailyDownloadsOverall,
    avgDailyDownloadsThisMonth,
    downloadsThisMonth,
    overallDays,
    downloadsAreExplicit,
    downloadDocCount: downloadDocs.length,
    segments: {
      profession: buildSegmentBreakdown(signups, segmentProfession),
      preferredFormat: buildSegmentBreakdown(signups, segmentPreferredFormat),
      projectTypes: buildSegmentBreakdown(signups, segmentProjectTypes),
      role: buildSegmentBreakdown(signups, segmentRole),
    },
    orderStatusSegments,
    planSegments,
    industrySegments,
    onboarding: {
      withProfession,
      withPreferredFormat: withFormat,
      withProjectTypes,
      withEmail,
      professionPct: signups.length ? (withProfession / signups.length) * 100 : null,
      formatPct: signups.length ? (withFormat / signups.length) * 100 : null,
      projectTypesPct: signups.length ? (withProjectTypes / signups.length) * 100 : null,
    },
    labelMaps: {
      profession: WORK_ROLE_LABELS,
      projectTypes: PROJECT_TYPE_LABELS,
      preferredFormat: PREFERRED_FORMAT_LABELS,
    },
    computedAt: now,
  };
}

export function growthDrillContent(growthKey, growth, contacts = []) {
  if (!growth) return null;
  const signups = contacts.filter((c) => !c.isInternal);

  switch (growthKey) {
    case "signups_today": {
      const key = growth.signupsTodayKey || localDayKey(Date.now());
      const rows = signups
        .filter((c) => {
          const ms = signupTimestampMs(c);
          return ms > 0 && localDayKey(ms) === key;
        })
        .sort((a, b) => signupTimestampMs(b) - signupTimestampMs(a))
        .map((c) => ({
          k: c.id,
          cells: [
            c.name || "—",
            c.email || "—",
            new Date(signupTimestampMs(c)).toLocaleString(),
          ],
          contact: c,
        }));
      return {
        subtitle: `${rows.length} sign-up(s) on ${key} (${growth.timezoneLabel || "local"}). Same calendar day as Contacts → Created.`,
        headers: ["Name", "Email", "Created (local)"],
        rows,
        showContactActions: true,
      };
    }
    case "daily_signups_history": {
      const rows = [...(growth.dailySignupsHistory || [])]
        .sort((a, b) => b.count - a.count)
        .map((r) => ({
          k: r.date,
          cells: [r.date, String(r.count), r.date === growth.peakDay?.date ? "Peak day" : ""],
        }));
      return {
        subtitle: `${growth.dailySignupsHistory?.length || 0} day(s) with at least one sign-up (${growth.timezoneLabel || "local"}). Sorted by count (highest first).`,
        headers: ["Date (local)", "Sign-ups", ""],
        rows,
        showContactActions: false,
      };
    }
    case "signups_this_month": {
      const key = monthKey(Date.now());
      const rows = signups
        .filter((c) => {
          const ms = signupTimestampMs(c);
          return ms > 0 && monthKey(ms) === key;
        })
        .sort((a, b) => signupTimestampMs(b) - signupTimestampMs(a))
        .map((c) => ({
          k: c.id,
          cells: [
            c.name || "—",
            c.email || "—",
            segmentProfession(c),
            new Date(signupTimestampMs(c)).toISOString().slice(0, 10),
          ],
          contact: c,
        }));
      return {
        subtitle: `${rows.length} sign-up(s) in ${monthLabel(key)}.`,
        headers: ["Name", "Email", "Profession", "Signed up"],
        rows,
        showContactActions: true,
      };
    }
    case "segment_profession":
    case "segment_format":
    case "segment_project":
    case "segment_role": {
      const segKey =
        {
          segment_profession: "profession",
          segment_format: "preferredFormat",
          segment_project: "projectTypes",
          segment_role: "role",
        }[growthKey] || "profession";
      const labelFn =
        {
          profession: segmentProfession,
          preferredFormat: segmentPreferredFormat,
          projectTypes: (c) => segmentProjectTypes(c).join(", "),
          role: segmentRole,
        }[segKey];
      const rows = signups
        .map((c) => ({
          k: c.id,
          cells: [c.name || "—", c.email || "—", labelFn(c)],
          contact: c,
        }))
        .sort((a, b) => String(a.cells[2]).localeCompare(String(b.cells[2])));
      return {
        subtitle: `${signups.length} non-internal profiles.`,
        headers: ["Name", "Email", segKey === "preferredFormat" ? "Preferred format" : segKey === "projectTypes" ? "Project types" : segKey === "role" ? "Role" : "Profession"],
        rows,
        showContactActions: true,
      };
    }
    default:
      return null;
  }
}

export { monthLabel, quarterLabel, slugLabel };
